// src/pages/role/RoleList.tsx

import React, { useState } from "react";
import { EDataTable } from "../../../components/EDataTable"; 
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { Edit, Trash2, Shield, Users } from "lucide-react"; // Yeni ikonlar
import { RoleDialog } from "./RoleDialog"; // Yeni Dialog
import { toast } from "react-toastify";

// Tipler ve Hook'lar
import { RoleType, RolePayload, RoleGroup } from "../../../types/role";
import { useRoleStore } from "../../../store/roleStore"; // Yeni Store
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types"; 


export const RoleList = () => {
    // Cash yerine roleStore kullanıyoruz
    const store = useRoleStore();
    // CRUD metotlarını store'dan çekiyoruz
    const { createItem, updateItem, deleteItem, data:roleGroups } = store; 
    const { handleApiError } = useApiErrorHandler();
    
    // --- STATE'LER ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [roleToEdit, setRoleToEdit] = useState<RoleType | null>(null);

    // --- ACTION HANDLERS (Aynı CRUD Mantığı) ---
    const handleNew = () => {
        setRoleToEdit(null);
        setIsModalOpen(true);
    };

    const handleEdit = (role: RoleType) => {
        setRoleToEdit(role);
        setIsModalOpen(true);
    };

    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (role: RoleType) => {
        // Silme uyarısı Role'e ait kullanıcıları içermeli
        const ok = await confirm(`${role.name} adlı rolü silmek istiyor musun? Bu role bağlı tüm kullanıcılar etkilenecektir!`);

        if (ok) {
            try {
                await deleteItem(role.id); // deleteItem yerine deleteRole
                toast.success("Rol silindi");
            } catch (error) {
                handleApiError(error); 
            }
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setRoleToEdit(null);
    };

    // RolePayload tipine uygun veriyi alır
    const handleSaveRole = async (roleData: RolePayload) => { 
        const cleanedData: RolePayload = { 
            ...roleData,
            // roleGroupId null/undefined gelirse temizle
            roleGroupId: roleData.roleGroupId || null 
        };
 
        try {
            if (roleToEdit) {
                // updateItem yerine updateRole
                await updateItem(roleToEdit.id, cleanedData); 
                toast.success(`Rol Güncellendi: ${cleanedData.name}`);
            } else {
                // createItem yerine createRole
                await createItem(cleanedData); 
                toast.success(`Yeni Rol Eklendi: ${cleanedData.name}`);
            }
            
        } catch (error) {
            handleApiError(error); 
        }
        handleCloseModal(); 
    };

    // --- DataTable body templates ---
    
    // Role Grubu adı
    const groupBodyTemplate = (role: RoleType) => (
        <span>{role.roleGroup || 'Yok'}</span>
    );
    
    // Role Seviyesi (Level)
    const levelBodyTemplate = (role: RoleType) => (
        <span className="font-semibold text-purple-700">{role.level}</span>
    );
    
    // Kullanıcı Sayısı (Eğer API'den geliyorsa)
    const userCountBodyTemplate = (role: RoleType) => (
        <div className="flex items-center justify-end gap-1 font-medium">
            <Users className="w-4 h-4 text-gray-500" />
            <span>{role.users?.length || 0}</span>
        </div>
    );
    
    const actionBodyTemplate = (role: RoleType) => (
        <TableActions
            actions={[
                {
                    icon: Edit,
                    colorClass: "text-blue-600 hover:bg-blue-50",
                    title: "Düzenle",
                    onClick: () => handleEdit(role)
                },
                {
                    icon: Trash2,
                    colorClass: "text-red-500 hover:bg-red-50",
                    title: "Sil",
                    onClick: () => handleDeleteClick(role)
                }
            ]}
        />
    );

    // Rol sütun tanımları
    const roleColumns: DataTableColumn<RoleType>[] = [
        { field: "id", header: "ID", width: "80px" },
        { 
            field: "name", 
            header: "Rol Adı", 
            width: "250px", 
            sortable: true, 
        },
        { 
            field: "level", 
            header: "Yetki Seviyesi", 
            width: "150px", 
            sortable: true,
            align: "center",
            body: levelBodyTemplate,
        },
        { 
            field: "roleGroup.name", 
            header: "Rol Grubu", 
            width: "180px", 
            body: groupBodyTemplate,
        },
        { 
            field: "userCount", 
            header: "Kullanıcı Sayısı", 
            width: "150px", 
            align: "right",
            body: userCountBodyTemplate,
        },
        { 
            field: "actions", 
            header: "İşlemler", 
            width: "120px", 
            align: "center",
            body: actionBodyTemplate 
        },
    ];

    return (
        <div className="w-full">
            {confirmModal}
            <EDataTable<RoleType> 
            tableKey="role_list"
                // useStore cashStore yerine roleStore
                useStore={() => store} 
                dataKey="id"
                title="Rol Listesi"
                placeholder="Rol Adı Ara"
                searchFields={[
                    { field: "name", operator: "contains" as SimpleFilter["operator"] },
                ]}
                actions={
                    <TextButton onClick={handleNew} className="btn-new">
                     Yeni Rol Ekle
                    </TextButton>
                }
                columns={roleColumns as any}
            />

            <RoleDialog
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                role={roleToEdit}
                onSave={handleSaveRole}
                // Role gruplarını dialog'a iletiyoruz
                roleGroups={roleGroups} 
            />
        </div>
    );
};